// Guest data on this device (guided guest flow, POC). The server stores
// nothing for a guest: the profile Pluto asked for (name, role, sub-role,
// workflow) and the guest's conversations live in localStorage under
// procureai.guest.v1, each stamped with createdAt and purged once older than
// 24 hours (on load, on every read and on an interval). The cooldown end the
// API answered with (after a round of messages) is kept there too, so a
// reload still shows the wait; the API's device cookie is what enforces it.
// Storage may be missing or full: the guest then simply starts fresh.
import { useSyncExternalStore } from "react";

export const GUEST_KEY = "procureai.guest.v1";
export const GUEST_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_CONVERSATIONS = 20;
const MAX_TURNS = 40;

export type GuestRole = "penyedia" | "non_penyedia";
export type GuestSubRole = "pa_kpa" | "ppk" | "pokja" | "pejabat_pengadaan" | "lainnya";

export interface GuestProfileLocal {
  name: string | null;
  role: GuestRole | null;
  subRole: GuestSubRole | null;
  workflow: string | null;
  /** Epoch ms; the profile expires with the same 24 hours. */
  createdAt: number;
}

export interface GuestTurn {
  role: "user" | "assistant";
  content: string;
  citations: string[];
  createdAt: string;
  /** The guest's inline file (name and type only; the bytes are never kept). */
  files?: { name: string; mimeType: string; size: number }[];
}

export interface GuestConversation {
  id: string;
  title: string;
  /** Epoch ms; purged 24 hours after this. */
  createdAt: number;
  updatedAt: number;
  messages: GuestTurn[];
}

export interface GuestState {
  profile: GuestProfileLocal | null;
  conversations: GuestConversation[];
  /** Epoch ms when the guest may send again (from the API's retryAfter); null when not waiting. */
  cooldownUntil: number | null;
}

const EMPTY: GuestState = { profile: null, conversations: [], cooldownUntil: null };

let clock: () => number = () => Date.now();
/** Tests inject a clock; null restores the real one. */
export function setGuestClock(fn: (() => number) | null): void {
  clock = fn ?? (() => Date.now());
  refresh();
}

export function guestNow(): number {
  return clock();
}

function parse(raw: string | null): GuestState {
  if (!raw) return EMPTY;
  try {
    const v = JSON.parse(raw) as Partial<GuestState>;
    return {
      profile: v.profile && typeof v.profile === "object" ? (v.profile as GuestProfileLocal) : null,
      conversations: Array.isArray(v.conversations) ? (v.conversations as GuestConversation[]).filter((c) => c && typeof c.id === "string" && Array.isArray(c.messages)) : [],
      cooldownUntil: typeof v.cooldownUntil === "number" && Number.isFinite(v.cooldownUntil) ? v.cooldownUntil : null,
    };
  } catch {
    return EMPTY;
  }
}

/** Drops everything older than 24 hours, and a cooldown that has ended. */
export function purged(state: GuestState, now = clock()): GuestState {
  const fresh = (t: number) => typeof t === "number" && now - t < GUEST_TTL_MS;
  return {
    profile: state.profile && fresh(state.profile.createdAt) ? state.profile : null,
    conversations: state.conversations.filter((c) => fresh(c.createdAt)),
    cooldownUntil: state.cooldownUntil !== null && state.cooldownUntil > now ? state.cooldownUntil : null,
  };
}

function load(): GuestState {
  try {
    return purged(parse(typeof localStorage === "undefined" ? null : localStorage.getItem(GUEST_KEY)));
  } catch {
    return EMPTY;
  }
}

let snapshot: GuestState = load();
const listeners = new Set<() => void>();

function store(next: GuestState): void {
  snapshot = next;
  try {
    if (!next.profile && next.conversations.length === 0 && next.cooldownUntil === null) localStorage.removeItem(GUEST_KEY);
    else localStorage.setItem(GUEST_KEY, JSON.stringify(next));
  } catch {
    // Full or blocked: this page only.
  }
  for (const l of listeners) l();
}

/** Re-reads storage and purges expired data (on load, on the interval, after a clock change). */
export function refresh(): void {
  const next = load();
  const changed = JSON.stringify(next) !== JSON.stringify(snapshot);
  snapshot = next;
  // Write the purge back so expired data leaves the device.
  try {
    const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(GUEST_KEY);
    if (raw !== null && JSON.stringify(parse(raw)) !== JSON.stringify(next)) store(next);
  } catch {
    // ignore
  }
  if (changed) for (const l of listeners) l();
}

export function getGuestState(): GuestState {
  return snapshot;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useGuestState(): GuestState {
  return useSyncExternalStore(subscribe, getGuestState, () => EMPTY);
}

/** Purges every minute while a guest page is open; returns the stop function. */
export function startGuestPurge(intervalMs = 60_000): () => void {
  refresh();
  const t = setInterval(refresh, intervalMs);
  return () => clearInterval(t);
}

export function updateGuestProfile(patch: Partial<Omit<GuestProfileLocal, "createdAt">>): GuestProfileLocal {
  const current = snapshot.profile ?? { name: null, role: null, subRole: null, workflow: null, createdAt: clock() };
  const profile = { ...current, ...patch };
  store({ ...snapshot, profile });
  return profile;
}

/** Ubah peran: the name stays, the role, sub-role and workflow are asked again. */
export function resetGuestRole(): void {
  if (!snapshot.profile) return;
  store({ ...snapshot, profile: { ...snapshot.profile, role: null, subRole: null, workflow: null } });
}

export function saveGuestConversation(conv: GuestConversation): void {
  const others = snapshot.conversations.filter((c) => c.id !== conv.id);
  const trimmed = { ...conv, messages: conv.messages.slice(-MAX_TURNS) };
  store({ ...snapshot, conversations: [trimmed, ...others].slice(0, MAX_CONVERSATIONS) });
}

/** Records when the guest may send again (null clears it). */
export function setGuestCooldown(until: number | null): void {
  store({ ...snapshot, cooldownUntil: until });
}

export function newGuestConversationId(): string {
  return `g-${clock().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/** Hours left before a conversation is purged (rounded up, at least 0). */
export function hoursLeft(c: GuestConversation, now = clock()): number {
  return Math.max(0, Math.ceil((c.createdAt + GUEST_TTL_MS - now) / 3_600_000));
}
